import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SlidersHorizontal, Trash2 } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSession } from "@/hooks/useSession";
import { api, type PolicyProfile } from "@/lib/api";

export default function Policies() {
  const qc = useQueryClient();
  const { user } = useSession();
  const isOwner = user?.role === "owner";
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [vars, setVars] = useState("");

  const { data } = useQuery({
    queryKey: ["policy-profiles"],
    queryFn: () => api.get<{ profiles: PolicyProfile[]; builtin: { id: string; name: string; description: string; custom: boolean }[] }>("/api/policy-profiles"),
  });
  const create = useMutation({
    mutationFn: () => {
      let parsed: Record<string, string> = {};
      for (const line of vars.split("\n")) {
        const t = line.trim();
        if (!t) continue;
        const eq = t.indexOf("=");
        if (eq < 1) throw new Error(`bad line: ${t} (expected KEY=value)`);
        parsed[t.slice(0, eq).trim()] = t.slice(eq + 1).trim();
      }
      return api.post("/api/policy-profiles", { name, description: desc, variables: parsed });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["policy-profiles"] });
      setName(""); setDesc(""); setVars("");
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/api/policy-profiles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policy-profiles"] }),
    onError: (e) => alert(String(e)),
  });

  const profiles = data?.profiles ?? [];
  const builtin = data?.builtin ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Policy profiles"
        description="Reusable BunkerWeb variable sets layered between the built-in application profile and per-app overrides. Inheritance: base → profile → plugin scope → app overrides → owner-disabled controls."
      />

      {/* built-in app profiles */}
      <Card>
        <div className="border-b px-4 py-3 text-sm font-medium">Built-in application profiles</div>
        <div className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {builtin.map((b) => (
            <div key={b.id} className="rounded-md border border-border/60 p-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                {b.name} <Badge variant="secondary">built-in</Badge>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{b.id}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* custom profiles */}
      <Card>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <span className="text-sm font-medium">Custom profiles</span>
        </div>
        <div className="divide-y">
          {profiles.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.description}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(p.variables).map(([k, v]) => (
                    <code key={k} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">
                      {k}={String(v)}
                    </code>
                  ))}
                </div>
              </div>
              {isOwner && (
                <Button size="sm" variant="ghost" onClick={() => del.mutate(p.id)} title="Delete profile">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          {profiles.length === 0 && (
            <EmptyState icon={SlidersHorizontal} title="No custom profiles"
              hint="Create a reusable variable set; apps opt in via their policy profile." />
          )}
        </div>
        {isOwner && (
          <div className="space-y-2 border-t p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New profile</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input placeholder="name" value={name} onChange={(e) => setName(e.target.value)} />
              <Input placeholder="description" value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>
            <textarea
              className="h-24 w-full rounded-md border bg-background p-2 font-mono text-xs"
              placeholder={"BunkerWeb variables, one per line:\nUSE_LIMIT_REQ=yes\nLIMIT_REQ_RATE=25r/s"}
              value={vars} onChange={(e) => setVars(e.target.value)}
            />
            <Button size="sm" disabled={name.trim().length < 2 || create.isPending}
              onClick={() => create.mutate()}>
              Create profile
            </Button>
            {create.isError && <div className="text-xs text-destructive">{String(create.error)}</div>}
          </div>
        )}
      </Card>
    </div>
  );
}
